/*
 * ---------------------------------------------------------------------------
 *  lm-gallery.js — vanilla JS controller for the LM product gallery.
 *  ----------------------------------------------------------------
 *  No jQuery, no slick. Each gallery wires itself up at load:
 *    - Click prev/next  → swap active main image (data-active attribute)
 *    - Click thumbnail  → jump to that index
 *    - Swipe main image → prev / next
 *    - Click thumb-strip up/down → scroll the thumb list one step
 *    - Keyboard ←/→     → prev/next when gallery is focused
 *
 *  All state lives on DOM attributes (`data-active`, `aria-current`)
 *  rather than on slick-style measurement, so layout-timing bugs that
 *  killed the previous implementation can't recur.
 * ---------------------------------------------------------------------------
 */
(function () {
  function init(root) {
    var slides     = Array.prototype.slice.call(root.querySelectorAll('[data-lm-slide]'));
    var thumbs     = Array.prototype.slice.call(root.querySelectorAll('[data-lm-thumb]'));
    var stage      = root.querySelector('[data-lm-stage]');
    var prevBtn    = root.querySelector('[data-lm-prev]');
    var nextBtn    = root.querySelector('[data-lm-next]');
    var thumbList  = root.querySelector('[data-lm-thumb-list]');
    var thumbPrev  = root.querySelector('[data-lm-thumb-prev]');
    var thumbNext  = root.querySelector('[data-lm-thumb-next]');

    if (!slides.length) return;

    var index = 0;

    function isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    function updateMainNav() {
      if (prevBtn) prevBtn.disabled = (index <= 0);
      if (nextBtn) nextBtn.disabled = (index >= slides.length - 1);
    }

    function updateThumbNav() {
      if (!thumbList || !(thumbPrev || thumbNext)) return;
      var atStart, atEnd;
      if (isMobile()) {
        atStart = thumbList.scrollLeft <= 1;
        atEnd   = thumbList.scrollLeft + thumbList.clientWidth >= thumbList.scrollWidth - 1;
      } else {
        atStart = thumbList.scrollTop <= 1;
        atEnd   = thumbList.scrollTop + thumbList.clientHeight >= thumbList.scrollHeight - 1;
      }
      if (thumbPrev) thumbPrev.disabled = atStart;
      if (thumbNext) thumbNext.disabled = atEnd;
    }

    /* Lazy-loader for deferred slides.
       --------------------------------
       Non-first slides are emitted with `data-src` / `data-srcset`
       (see lm-gallery.liquid notes). Swapping those over to the
       real attributes is what triggers the browser to fetch the
       image. This function is called twice per setIndex():
         - For the active slide  → ensure it's loaded NOW
         - For the next slide    → proactively preload it so a
                                   subsequent prev/next click feels
                                   instant rather than waiting on
                                   the network.
       Idempotent: once the swap runs, `data-src` is removed so
       repeated calls do nothing. */
    function hydrateSlide(slide) {
      if (!slide) return;
      var img = slide.querySelector('img[data-lm-defer]');
      if (!img) return;
      var src = img.getAttribute('data-src');
      var srcset = img.getAttribute('data-srcset');
      if (src) {
        img.setAttribute('src', src);
        img.removeAttribute('data-src');
      }
      if (srcset) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-srcset');
      }
      img.removeAttribute('data-lm-defer');
    }

    function setIndex(next) {
      if (next < 0) next = 0;
      if (next > slides.length - 1) next = slides.length - 1;
      index = next;

      slides.forEach(function (slide, i) {
        if (i === index) slide.setAttribute('data-active', 'true');
        else slide.removeAttribute('data-active');
      });
      thumbs.forEach(function (thumb, i) {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // Load the active slide's image immediately (if it was deferred)
      // and proactively preload the neighbours so prev/next clicks
      // don't wait on the network.
      hydrateSlide(slides[index]);
      if (slides[index + 1]) hydrateSlide(slides[index + 1]);
      if (slides[index - 1]) hydrateSlide(slides[index - 1]);

      // Keep the active thumb visible. `scrollIntoView` with `nearest`
      // only scrolls if the element is outside the viewport — clicks on
      // thumbs already in view leave the strip stationary.
      var activeThumb = thumbs[index];
      if (activeThumb && activeThumb.scrollIntoView) {
        activeThumb.scrollIntoView({
          block: isMobile() ? 'nearest' : 'nearest',
          inline: isMobile() ? 'nearest' : 'nearest',
          behavior: 'smooth'
        });
      }

      updateMainNav();
      // updateThumbNav runs after the smooth-scroll has a chance to fire.
      window.setTimeout(updateThumbNav, 350);
    }

    // --- Wire up controls --------------------------------------------------
    if (prevBtn) prevBtn.addEventListener('click', function () { setIndex(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setIndex(index + 1); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setIndex(i); });
    });

    if (thumbPrev && thumbList) {
      thumbPrev.addEventListener('click', function () {
        if (isMobile()) thumbList.scrollBy({ left: -120, behavior: 'smooth' });
        else            thumbList.scrollBy({ top:  -120, behavior: 'smooth' });
        window.setTimeout(updateThumbNav, 350);
      });
    }
    if (thumbNext && thumbList) {
      thumbNext.addEventListener('click', function () {
        if (isMobile()) thumbList.scrollBy({ left: 120, behavior: 'smooth' });
        else            thumbList.scrollBy({ top:  120, behavior: 'smooth' });
        window.setTimeout(updateThumbNav, 350);
      });
    }

    // --- Touch swipe on the main stage ------------------------------------
    if (stage && 'ontouchstart' in window) {
      var touchStartX = 0;
      var touchStartY = 0;
      stage.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        // Only treat as a swipe if predominantly horizontal + > 50px.
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) setIndex(index + 1);
          else        setIndex(index - 1);
        }
      }, { passive: true });
    }

    // --- Keyboard navigation when the gallery has focus -------------------
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { setIndex(index - 1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { setIndex(index + 1); e.preventDefault(); }
    });

    // --- Update thumb nav on user-initiated scroll ------------------------
    if (thumbList) {
      thumbList.addEventListener('scroll', updateThumbNav, { passive: true });
    }

    // Re-evaluate on resize (mobile/desktop boundary changes scroll axis).
    var resizeRaf;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(updateThumbNav);
    });

    setIndex(0);
  }

  function boot() {
    var galleries = document.querySelectorAll('[data-lm-gallery]');
    Array.prototype.forEach.call(galleries, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
